import {
  DEFAULT_CALENDAR_SALES_ORDER_SORT,
  DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
  DEFAULT_GRID_SALES_ORDER_SORT,
  applyLegacySalesOrderFilterParams,
  applyLegacySalesOrderSourceFilterParams,
  defaultSalesOrderSearchParams,
  getCurrentSalesOrderCalendarMonth,
  getImplicitSalesOrderFilterKeys,
  getSalesOrderCalendarMonth,
  getSalesOrderCalendarVisibleDateRange,
  hasExplicitSearchParam,
  normalizeSalesOrderSort,
  salesOrderFilterDefaults,
  type SalesOrderSearchRecord,
} from './sales-order.schema'

const SALES_ORDER_DATE_FILTER_KEYS = ['due', 'due_range', 'created', 'created_range']

export function getSalesOrderLayoutDefaultSort(layout: string | undefined): string {
  if (layout === 'grid') {
    return DEFAULT_GRID_SALES_ORDER_SORT
  }

  if (layout === 'calendar') {
    return DEFAULT_CALENDAR_SALES_ORDER_SORT
  }

  return defaultSalesOrderSearchParams.sort
}

type SalesOrderUrlState = SalesOrderSearchRecord & { layout: string; sort: string }
type PaginatedSalesOrderSearch = SalesOrderSearchRecord & { page: number; page_size: number }

export function getSalesOrderUrlSearchParams(
  search: PaginatedSalesOrderSearch
): SalesOrderUrlState & { page: number; page_size: number }
export function getSalesOrderUrlSearchParams(search?: SalesOrderSearchRecord): SalesOrderUrlState
export function getSalesOrderUrlSearchParams(
  search: SalesOrderSearchRecord = {}
): SalesOrderUrlState {
  const normalizedSearch = applyLegacySalesOrderSourceFilterParams(search, search)
  const layout = normalizedSearch.layout ?? defaultSalesOrderSearchParams.layout
  const defaultSort = getSalesOrderLayoutDefaultSort(layout)
  const searchParams: SalesOrderUrlState = {
    ...defaultSalesOrderSearchParams,
    ...normalizedSearch,
    layout,
    fulfillment_status_filter:
      normalizedSearch.fulfillment_status_filter ??
      (layout === 'grid' && !hasExplicitSearchParam(normalizedSearch, 'filter')
        ? DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER
        : undefined),
    sort: normalizeSalesOrderSort(normalizedSearch.sort) ?? defaultSort,
  }

  if (layout === 'calendar') {
    const month = getSalesOrderCalendarMonth(normalizedSearch)

    SALES_ORDER_DATE_FILTER_KEYS.forEach((key) => {
      delete searchParams[key]
    })

    searchParams.month = month
  }

  return searchParams
}

export function getSalesOrderNavigationSearchParams(
  search: SalesOrderSearchRecord = {}
): SalesOrderSearchRecord {
  const searchParams: SalesOrderSearchRecord = applyLegacySalesOrderFilterParams(
    getSalesOrderUrlSearchParams(search),
    search
  )

  if (
    searchParams.filter === defaultSalesOrderSearchParams.filter &&
    !hasExplicitSearchParam(search, 'filter')
  ) {
    delete searchParams.filter
  }

  Object.entries(salesOrderFilterDefaults).forEach(([key, value]) => {
    if (searchParams[key] === value && !hasExplicitSearchParam(search, key)) {
      delete searchParams[key]
    }
  })

  if (searchParams.layout === 'grid') {
    if (
      !hasExplicitSearchParam(search, 'filter') &&
      !hasExplicitSearchParam(search, 'fulfillment_status_filter')
    ) {
      delete searchParams.fulfillment_status_filter
    }

    if (!hasExplicitSearchParam(search, 'sort')) {
      delete searchParams.sort
    }
  }

  return searchParams
}

export function getSalesOrderCalendarDateRange(search: SalesOrderSearchRecord = {}): string {
  return getSalesOrderCalendarVisibleDateRange(getSalesOrderCalendarMonth(search))
}

export function getSalesOrderCalendarSummaryFilters(
  filters: SalesOrderSearchRecord = {},
  calendarDateRange?: string
): SalesOrderSearchRecord {
  const summaryFilters = { ...filters }

  delete summaryFilters.page
  delete summaryFilters.page_size
  delete summaryFilters.sort

  SALES_ORDER_DATE_FILTER_KEYS.forEach((key) => {
    delete summaryFilters[key]
  })

  if (calendarDateRange) {
    summaryFilters.due_range = calendarDateRange
  }

  return summaryFilters
}

export function getSalesOrderLayoutChangeParams(
  search: SalesOrderSearchRecord = {},
  nextLayout?: string
): SalesOrderSearchRecord {
  const currentSearchParams = getSalesOrderUrlSearchParams(search)
  const currentLayout = currentSearchParams.layout
  const currentLayoutDefaultSort = getSalesOrderLayoutDefaultSort(currentLayout)
  const nextLayoutDefaultSort = getSalesOrderLayoutDefaultSort(nextLayout)
  const implicitFilterKeys = getImplicitSalesOrderFilterKeys(search)
  const updates: SalesOrderSearchRecord = { layout: nextLayout }
  const shouldClearLayoutDefaultSort =
    currentLayoutDefaultSort !== nextLayoutDefaultSort &&
    (!hasExplicitSearchParam(search, 'sort') ||
      currentSearchParams.sort === currentLayoutDefaultSort)

  if (shouldClearLayoutDefaultSort || nextLayout === 'calendar') {
    updates.sort = undefined
  }

  if (nextLayout === 'calendar') {
    const month = getSalesOrderCalendarMonth(search)

    updates.month = month === getCurrentSalesOrderCalendarMonth() ? undefined : month
    SALES_ORDER_DATE_FILTER_KEYS.forEach((key) => {
      updates[key] = undefined
    })
  }

  if (nextLayout === 'grid') {
    updates.sort = undefined

    if (
      currentSearchParams.fulfillment_status_filter ===
      defaultSalesOrderSearchParams.fulfillment_status_filter
    ) {
      updates.fulfillment_status_filter = undefined
    }
  }

  if (currentLayout === 'grid' && nextLayout !== 'grid') {
    if (implicitFilterKeys.includes('fulfillment_status_filter')) {
      updates.fulfillment_status_filter = undefined
    }

    if (implicitFilterKeys.includes('sort')) {
      updates.sort = undefined
    }
  }

  return updates
}
